import { Injectable, inject, signal } from '@angular/core';
import { TenantConfig } from '../config/tenant-config';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private currentTenant = signal<TenantConfig | null>(null);
  public currentTenant$ = this.currentTenant.asReadonly();

  constructor() {
    // Try to initialize from API config first (set in main.ts)
    const apiConfig = (window as any).__tenantConfig;
    if (apiConfig) {
      console.log('[TenantService] initialized from window.__tenantConfig', apiConfig);
      this.currentTenant.set(apiConfig);
    } else {
      const isErrorPage = typeof window !== 'undefined' && window.location.pathname === '/error';
      if (isErrorPage) {
        console.log('[TenantService] window.__tenantConfig missing on /error, using environment config');
        // Fallback to environment config for error page only
        this.initializeTenant();
        console.log('[TenantService] initialized from environment', this.currentTenant());
      } else {
        console.error('[TenantService] window.__tenantConfig missing. Tenant must be initialized before app bootstrap.');
      }
    }
  }

  /**
   * Initialize tenant from API response
   * Called from main.ts after tenant validation
   */
  initializeFromApi(tenantConfig: TenantConfig) {
    console.log('[TenantService] initializeFromApi()', tenantConfig);
    this.currentTenant.set(tenantConfig);
  }

 private initializeTenant() {
    // Load tenant config from environment (set at build time) - fallback only
    const tenantConfig: TenantConfig = {
      tenantId: environment.tenantId || 'platform',
      tenantName: "Default Tenant",
      firebaseMode: 'shared',
      firebaseConfig: environment.firebase,
      features: {},
      branding:{ },
      domain: "localhost",
    };

    this.currentTenant.set(tenantConfig);
  }
  getCurrentTenant(): TenantConfig | null {
    return this.currentTenant();
  }

  getTenantId(): string {
    const tenantId = this.currentTenant()?.tenantId;
    if (!tenantId) {
      throw new Error('Tenant is not initialized');
    }
    return tenantId;
  }

  getFirebaseMode(): 'shared' | 'separate' {
    const mode = this.currentTenant()?.firebaseMode || 'shared';
    return mode;
  }

  isSharedFirebase(): boolean {
    return this.getFirebaseMode() === 'shared';
  }

  /**
   * Get collection path with tenant ID in nested structure
   * Returns: data/sms/schools/{tenantId}/{collectionName} for shared Firebase
   * Returns: {collectionName} for separate Firebase
   */
  getCollectionName(baseName: string): string {
    const tenantId = this.getTenantId();
    if (this.isSharedFirebase()) {
      const path = `data/sms/schools/${tenantId}/${baseName}`;
      console.log('[TenantService] getCollectionPath->'+path);
      return path;
    }
    console.log('[TenantService] getCollectionPath ->'+baseName);
    return baseName;
  }

  /**
   * Get the current app mode (defaults to 'admin')
   */
  getAppMode(): 'admin' | 'full' {
    return this.currentTenant()?.appMode === 'full' ? 'full' : 'admin';
  }

  /**
   * Update app mode locally (after saving to Firestore).
   */
  setAppMode(mode: 'admin' | 'full'): void {
    const current = this.currentTenant();
    if (current) {
      this.currentTenant.set({ ...current, appMode: mode });
    }
  }

  /**
   * Check if a feature is enabled for current tenant
   */
  isFeatureEnabled(feature: keyof NonNullable<TenantConfig['features']>): boolean {
    return this.currentTenant()?.features?.[feature] ?? true;
  }

  /**
   * Get tenant branding configuration
   */
  getBranding() {
    return this.currentTenant()?.branding || null;
  }

  /**
   * Get primary brand color
   */
  getPrimaryColor(): string {
    return this.currentTenant()?.branding?.primaryColor || '#1A237E';
  }

  /**
   * Get secondary brand color
   */
  getSecondaryColor(): string {
    return this.currentTenant()?.branding?.secondaryColor || '#E67E22';
  }

  /**
   * Get tenant logo URL
   */
  getLogo(): string | undefined {
    return this.currentTenant()?.branding?.logo;
  }

  /**
   * Get tenant/school name for branding
   */
  getBrandName(): string {
    return this.currentTenant()?.branding?.name || this.currentTenant()?.tenantName || 'School';
  }
}

